import { computed, Directive, inject, InjectionToken, input, linkedSignal, Signal } from '@angular/core';
import { HisRouteService } from '../service/routing/his-route.service';
import { ensurePrefix, removePrefix, trimToEmpty } from '../utils';
import { splitTrimmed } from '../utils/his-string.utils';

// ====================
//      DD Contexts
// ====================

// ranks contexts by priority: DEFAULT first, then service/, then view/, then others (viewContext is the first 'view/' in the final array, after explicit 'service/' contexts
const rank = ( c: string ) => c === 'DEFAULT' ? 0 : c.startsWith( 'service/' ) ? 1 : c.startsWith( 'view/' ) ? 2 : 3;

@Directive( {
    // auto attach to all hisDdKey to guarantee injection there and replace its [hisDdContexts] input binding, but allow standalone usage
    selector: '[hisDdKey],[hisDdContexts]',
    standalone: true,
} )
export class HisDdContextsDirective {
    /** Input binding for the DD contexts, can be a string or an array of strings. Will be hydrated with the implicit view context and `DEFAULT` context internally. */
    public readonly contextsInput = input<string[] | string>( undefined, { alias: 'hisDdContexts' } );
    /** The implicit view context derived from the current route (or the custom override provided by the `hisViewContext` directive) */
    private readonly viewContext = inject( HIS_VIEW_CONTEXT );
    // could be extended with additional context sources in the future (e.g. 'serviceContext', 'formContext', etc.)

    /** Signal containing the parsed and ordered DD contexts */
    private readonly contexts = linkedSignal( () => this.parseContexts( this.contextsInput() ) );
    /** Exposed readonly signal containing the parsed and ordered DD contexts */
    public readonly hisDdContexts = this.contexts.asReadonly();

    /**
     * Sets the DD contexts explicitly, overriding the current parsed contexts.
     * Programmatic alternative to the `[hisDdContexts]` input binding.
     */
    public setHisDdContexts( contexts: string[] | string ) {
        this.contexts.set( this.parseContexts( contexts ) );
    }

    /** Retrieves the current parsed and ordered DD contexts */
    public getHisDdContexts(): string[] {
        return this.contexts();
    }

    /** Normalizes and orders the available contexts */
    private parseContexts( contexts: string[] | string ): string[] {
        const normalizedContexts = typeof contexts === 'string' ? splitTrimmed( contexts, ',' ).filter( Boolean ) : contexts ?? [];
        return [...new Set( ['DEFAULT', this.viewContext(), ...normalizedContexts] )]
            .sort( ( a, b ) => rank( a ) - rank( b ) );
    }
}


// ====================
//     View Context
// ====================

/** Represents the view context as a signal containing a string value */
export type ViewContext = Signal<string>;

/** Injection token for the view context signal. Will always provide the current view context as a signal. */
export const HIS_VIEW_CONTEXT = new InjectionToken<ViewContext>( 'VIEW_CONTEXT', {
    providedIn: 'root',
    factory() {
        const routeService = inject( HisRouteService );
        return computed( () => {
            const module = routeService.getUrlElement( 'module' );
            // replaces every param with a placeholder token ':p' to normalize the route path and reinforce it against refactorings to param names
            const normalizedRoutePath = routeService.getRoutePathTemplate().replaceAll( /:([a-zA-Z0-9_]+)/g, ':p' );
            return `view/${module}${normalizedRoutePath}`; // final view context: 'view/cs.sys.hisng/page/edit/:p/tab/:p'
        } );
    },
} );

/**
 * Directive to provide an explicit, scoped view context to override the implicit view context derived from the current route.
 * Applies to all input elements within the current the scope of the directive (child elements).
 * ```html
 * // derived implicit view context: 'view/cs.sys.hisng/page/edit'
 * <div hisViewContext="cs.sys.hisng.customContext">
 *     // plam inputs within this div use 'view/cs.sys.hisng.customContext' as the implicit view context
 *     <his-plam ...>
 *         // explicit contexts on specific fields are added to the implicit contexts
 *         <input #content hisDdKey="someKey" [hisDdContexts]="['service/cs.sys.hisng.customContext']" ... />
 *     </his-plam>
 * </div>
 * ```
 * The final context array for the field above would be:\
 * `['DEFAULT', 'service/cs.sys.hisng.customContext', 'view/cs.sys.hisng.customContext']`
 * 
 * Without the explicit `[hisViewContext]` directive, the implicit view context is derived from the current route:\
 * `['DEFAULT', 'service/cs.sys.hisng.customContext', 'view/cs.sys.hisng.page/edit']`
 * 
 * **Note:**
 * - The 'DEFAULT' context is always included as the first element in the final context array.
 * - Custom `service/...` [hisDdContexts] are inserted before the implicit `view/...` context.
 * - Custom `view/...` [hisDdContexts] are appended after the implicit `view/...` context.
 * 
 * This matches the JSF DD-Context behavior.
 */
@Directive( {
    selector: '[hisViewContext]',
    standalone: true,
    providers: [
        {
            provide: HIS_VIEW_CONTEXT,
            useFactory: ( directive: HisViewContextDirective ): ViewContext => directive.hisViewContext,
            deps: [HisViewContextDirective]
        }
    ],
} )
export class HisViewContextDirective {
    readonly hisViewContext = input.required<string, string>( { transform: v => ensurePrefix( removePrefix( trimToEmpty( v ), '/'), 'view/' ) } );
}